/**
 * TypeScript interfaces for Airtable records
 * Base ID: apppu8LnhxQYsDAI7
 */

import type { BaseRecord } from "@refinedev/core";

/**
 * Base interface for all Airtable records
 * Extends Refine's BaseRecord which requires an id field
 */
export interface AirtableRecord extends BaseRecord {
  id: string;
  createdTime?: string;
}

/**
 * Airtable attachment object structure
 * Used for file/image fields in Airtable
 */
export interface AirtableAttachment {
  id?: string;
  url: string;
  filename?: string;
  size?: number;
  type?: string;
  width?: number;
  height?: number;
  thumbnails?: {
    small?: { url: string; width: number; height: number };
    large?: { url: string; width: number; height: number };
    full?: { url: string; width: number; height: number };
  };
}

/**
 * Client Database table (tblqUzsHrbTh9xy3z)
 * Stores user profiles and account data
 *
 * Includes both normalized camelCase names and Airtable field names
 */
export interface ClientDatabase extends AirtableRecord {
  // === Normalized camelCase names ===
  // Identity fields
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;

  // Authentication
  magicLinkToken?: string;
  magicLinkExpiry?: string;
  googleId?: string;

  // Account status
  accountStatus?: "active" | "inactive" | "suspended";
  createdAt?: string;
  lastLoginAt?: string;

  // Profile data
  phone?: string;
  timezone?: string;
  preferredLanguage?: string;

  // Credits
  creditBalance?: number;

  // Linked records
  submissions?: string[];
  calls?: string[];
  creditTransactions?: string[];
  journeySteps?: string[];

  // === Airtable field names (as returned from API) ===
  "EMail"?: string;
  "Name"?: string;
  "Phone Number"?: string;
  "Business Type"?: string;
  "Coaching Request"?: string;
  "Profile Image - Softr"?: AirtableAttachment[];
  "Client Timezone"?: string;
  "Preferred Day of Week"?: string;
  "Preferred Time"?: string;
  "Auto-Call Coaching"?: "On" | "Off";
}

/**
 * Submission Data table (tbl3xzuzMlHrDvuom)
 * Stores Voice AI coaching submissions
 * Field names match actual Airtable column names
 */
export interface SubmissionData extends AirtableRecord {
  // User info
  "EMail": string;
  "Name"?: string;
  "Phone Number"?: string;

  // Submission content
  "Goals and/or Challenges"?: string;

  // Coach selection (displays as "Matilda - Life Coach", etc.)
  "Configuration"?: string;

  // Timestamps
  "Created"?: string;
  "Time Call Started"?: string;

  // System fields
  "Submission Record"?: string;
  "Manually Trigger Run"?: string;

  // Token usage
  "Prompt Tokens"?: number;
  "Completion Tokens"?: number;
  "Token Cost ($USD)"?: number;
  "Vapi Cost"?: number;

  // Linked records
  "Client Database"?: string[];
  "Call Data"?: string[];
  "Vapi Initial Response"?: string[];

  // Device info
  "Device-Type"?: string;
  "IP Address"?: string;

  // Call scheduling
  "Number of Calls Owed"?: number;
}

/**
 * Submission status type for history display
 * Derived from whether Call Data exists
 */
export type SubmissionStatus = "pending" | "completed";

/**
 * Call Data table (tblMLpCB2q4EpVEdJ)
 * Stores call history and recordings
 *
 * Includes both Airtable field names and normalized camelCase names.
 * The dataProvider normalizes Airtable fields via airtable-field-mapper.ts
 */
export interface CallData extends AirtableRecord {
  // === Airtable field names (as returned from API) ===
  "Customer Name"?: string;
  "Email"?: string;
  "Client Database"?: string[];
  "Type"?: string;
  "Ended Reason"?: string;
  "Duration Seconds"?: number;
  "Created"?: string;
  "Call Recording"?: string;
  "Transcript"?: string;
  "Summary"?: string;
  "Insights and Reports"?: string;
  "High Level Consult"?: string;
  "Journeyed"?: string;
  "Call Image"?: unknown[];
  "Vapi Response - Status Code"?: string;
  "Vapi Response - Data"?: string;

  // === Normalized camelCase names (from airtable-field-mapper) ===
  customerName?: string;
  clientEmail?: string;
  clientDatabase?: string[];
  callType?: string;
  endedReason?: string;
  durationSeconds?: number;
  recordingUrl?: string;
  transcript?: string;
  summary?: string;
  insightsAndReports?: string;
  highLevelConsult?: string;
  journeyed?: string;
  callImage?: unknown[];
  vapiStatusCode?: string;
  vapiResponseData?: string;
}

/**
 * Credits Ledger table (tblH1H9RRRxMGWxOM)
 * Stores credit transactions and balances
 */
export interface CreditsLedger extends AirtableRecord {
  // Client reference
  clientId: string;
  clientEmail?: string;

  // Transaction details
  transactionType: "purchase" | "usage" | "refund" | "bonus" | "adjustment";
  amount: number;
  balanceAfter?: number;

  // Transaction metadata
  description?: string;
  transactionDate: string;

  // Payment reference
  stripePaymentId?: string;
  stripeInvoiceId?: string;

  // Linked records
  submissionId?: string;
  callId?: string;
}

/**
 * Configuration table (tblb46FmjUIrs0IdP)
 * Stores AI coach configurations
 */
export interface Configuration extends AirtableRecord {
  // Identity
  Name: string;
  "Record ID"?: string;
  "Clean Name"?: string;

  // Voice settings
  "Voice ID"?: string;
  "Voice Provider"?: string;
  "Voice Model"?: string;
  "Voice Stability"?: number;
  "Voice Similarity"?: number;
  "Voice Style"?: string;
  "Boost Speaker"?: string;

  // Transcriber settings
  "Transcriber Provider"?: string;
  "Transcriber Language"?: string;
  "Transcriber Model"?: string;

  // Instructions
  "Instructions - New-Client"?: string;
  "Instructions - Follow-Up"?: string;
  "Opening Sentence"?: string;
}

/**
 * Client's Journey table (tblQlHLDPYwsmT82u)
 * Stores onboarding and progress tracking
 */
export interface ClientJourney extends AirtableRecord {
  // Client reference
  clientId: string;
  clientEmail?: string;

  // Journey step details
  stepName: string;
  stepOrder: number;
  stepCategory?: "onboarding" | "engagement" | "milestone" | "achievement";

  // Progress tracking
  status: "not_started" | "in_progress" | "completed" | "skipped";
  startedAt?: string;
  completedAt?: string;

  // Content
  description?: string;
  actionRequired?: string;
  resourceUrl?: string;

  // Metrics
  pointsEarned?: number;
}

/**
 * Airtable table IDs for resource configuration
 */
export const AIRTABLE_TABLES = {
  CLIENT_DATABASE: "tblqUzsHrbTh9xy3z",
  SUBMISSION_DATA: "tbl3xzuzMlHrDvuom",
  CALL_DATA: "tblMLpCB2q4EpVEdJ",
  CREDITS_LEDGER: "tblH1H9RRRxMGWxOM",
  CLIENT_JOURNEY: "tblQlHLDPYwsmT82u",
  CONFIGURATION: "tblb46FmjUIrs0IdP",
} as const;

/**
 * Type alias for table ID values
 */
export type AirtableTableId = (typeof AIRTABLE_TABLES)[keyof typeof AIRTABLE_TABLES];
